import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { queryClient } from '../components/queryClient';
import { useFleet } from '../hooks/useFleet';
import { useHasPermission } from '../hooks/useHasPermission';
import { ConfigType, ConfigurationManagementService, EntityType } from '../services/configurationManagement.service';
import { useFeatureFlags } from './featureFlags';

const QUERY_KEY_BASE = 'changeRequests';

/**
 * Hook to fetch a single change request by ID
 */
export const useChangeRequestById = (id: number) => {
  const hasPermission = useHasPermission('configurations:read:change_requests');
  const { ccr_enabled } = useFeatureFlags();

  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'byId', id],
    queryFn: () => ConfigurationManagementService.getById(id),
    enabled: ccr_enabled === true && !!id && hasPermission,
  });
};

export interface UseChangeRequestsOptions {
  enabled?: boolean;
  configType?: ConfigType;
}

/**
 * Hook to fetch change requests for a specific entity.
 * @param entityType - The type of entity (e.g. 'fleet', 'authority')
 * @param entityId - The ID of the entity
 * @param options - Options object with enabled and configType
 * @returns Query result with additional pendingChangeRequests array and hasPendingChangeRequest boolean
 */
export const useChangeRequests = (entityType: EntityType, entityId: string, options?: UseChangeRequestsOptions) => {
  const hasPermission = useHasPermission('configurations:read:change_requests');
  const { ccr_enabled } = useFeatureFlags();
  const queryKey = [QUERY_KEY_BASE, entityType, entityId];

  const { enabled = true, configType } = options ?? {};

  const query = useQuery({
    queryKey,
    queryFn: () => ConfigurationManagementService.listChangeRequests(entityType, entityId),
    enabled: ccr_enabled === true && !!entityId && enabled && hasPermission,
  });

  const pendingChangeRequests = useMemo(() => {
    if (!ccr_enabled || !Array.isArray(query.data)) {
      return [];
    }

    return query.data.filter((cr) => cr.status === 'pending' && (!configType || cr.config_type === configType));
  }, [query.data, configType, ccr_enabled]);

  return {
    ...query,
    pendingChangeRequests,
    hasPendingChangeRequest: pendingChangeRequests.length > 0,
  };
};

/**
 * Hook to fetch all change requests for the current fleet.
 */
export const useFleetChangeRequests = (options?: UseChangeRequestsOptions) => {
  const fleet = useFleet();

  return useChangeRequests('fleet', fleet.uuid, options);
};

export const forceRefreshChangeRequests = () => {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BASE] });
};
